/**
 * Timezone utility functions for handling company-specific timezone conversions
 * All dates from the API are in UTC format and need to be converted to company timezone
 */

import i18n from '../i18n';
import { TIMEZONE_OPTIONS } from './constants';

export const getDetectedTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const formatTimezoneLabel = (tz: string): string => {
  try {
    const now = new Date();
    const utcStr = now.toLocaleString('en-US', { timeZone: 'UTC' });
    const tzStr = now.toLocaleString('en-US', { timeZone: tz });
    const utcDate = new Date(utcStr);
    const tzDate = new Date(tzStr);
    const diffMs = tzDate.getTime() - utcDate.getTime();
    const sign = diffMs >= 0 ? '+' : '-';
    const absDiff = Math.abs(diffMs);
    const hours = Math.floor(absDiff / 3600000);
    const minutes = Math.floor((absDiff % 3600000) / 60000);
    const offset = minutes > 0
      ? `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
      : `UTC${sign}${String(hours).padStart(2, '0')}:00`;
    return `${tz} (${offset})`;
  } catch {
    return tz;
  }
};

let cachedTimezoneOptions: { value: string; label: string }[] | null = null;

export const getTimezoneOptions = (): { value: string; label: string }[] => {
  if (cachedTimezoneOptions) return cachedTimezoneOptions;

  const detected = getDetectedTimezone();
  let timezones: string[] = [];

  try {
    const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
    if (typeof intl.supportedValuesOf === 'function') {
      timezones = intl.supportedValuesOf('timeZone');
    }
  } catch {
    // fallback
  }

  if (timezones.length === 0) {
    const fallback = [...TIMEZONE_OPTIONS].sort((a, b) => {
      if (a.value === detected) return -1;
      if (b.value === detected) return 1;
      return a.value.localeCompare(b.value);
    });
    cachedTimezoneOptions = fallback;
    return fallback;
  }

  if (!timezones.includes('UTC')) {
    timezones = ['UTC', ...timezones];
  }

  const options = timezones.map((tz) => ({
    value: tz,
    label: formatTimezoneLabel(tz)
  }));

  options.sort((a, b) => {
    if (a.value === detected) return -1;
    if (b.value === detected) return 1;
    return a.value.localeCompare(b.value);
  });

  cachedTimezoneOptions = options;
  return options;
};

/**
 * Convert UTC date string to company timezone
 * @param utcDateString - UTC date string from API (e.g., "2025-08-18T14:30:00.000000Z")
 * @param companyTimezone - Company timezone (e.g., "Asia/Dubai")
 * @returns Date object in company timezone
 */
export const convertUTCToCompanyTimezone = (utcDateString: string, companyTimezone: string): Date => {
  if (!utcDateString) return new Date();
  
  try {
    // Create UTC date object
    const utcDate = new Date(utcDateString);
    
    // Convert to company timezone
    const companyDate = new Date(utcDate.toLocaleString('en-US', { timeZone: companyTimezone }));
    
    // Calculate timezone offset to preserve the actual time
    const utcTime = utcDate.getTime();
    const companyTime = companyDate.getTime();
    const offset = companyTime - utcTime;
    
    // Apply offset to get correct time in company timezone
    return new Date(utcTime + offset);
  } catch (error) {
    console.error('Error converting UTC to company timezone:', error);
    // Fallback to original date if conversion fails
    return new Date(utcDateString);
  }
};

/**
 * Format date in company timezone
 * @param utcDateString - UTC date string from API
 * @param companyTimezone - Company timezone
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date string in company timezone
 */
export const formatDateInCompanyTimezone = (
  utcDateString: string, 
  companyTimezone: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  if (!utcDateString) return 'N/A';
  
  try {
    const companyDate = convertUTCToCompanyTimezone(utcDateString, companyTimezone);
    
    // Default options for date formatting
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...options
    };
    
    return companyDate.toLocaleDateString('en-US', defaultOptions);
  } catch (error) {
    console.error('Error formatting date in company timezone:', error);
    return 'N/A';
  }
};


const normalizeToUTC = (dateString: string): string => {
  const s = dateString.trim().replace(' ', 'T');
  if (s.endsWith('Z')) return s;
  if (/[+-]\d{2}:\d{2}$/.test(s)) return s;
  return s + 'Z';
};

const TIME_ONLY_PATTERN = /^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;

const parseTimeOnlyString = (timeString: string): { hour: number; minute: number; second: number } | null => {
  const match = timeString.trim().match(TIME_ONLY_PATTERN);
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const second = match[3] ? Number(match[3]) : 0;

  if (
    Number.isNaN(hour) ||
    Number.isNaN(minute) ||
    Number.isNaN(second) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    second < 0 ||
    second > 59
  ) {
    return null;
  }

  return { hour, minute, second };
};

const padTimePart = (value: number): string => String(value).padStart(2, '0');

const formatTimeParts = ({ hour, minute, second }: { hour: number; minute: number; second: number }): string => {
  return `${padTimePart(hour)}:${padTimePart(minute)}:${padTimePart(second)}`;
};

const getDateTimePartsInTimeZone = (date: Date, timeZone: string) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  });

  const parts = formatter.formatToParts(date);
  const getPart = (type: Intl.DateTimeFormatPartTypes): number => {
    return Number(parts.find((part) => part.type === type)?.value ?? 0);
  };

  return {
    year: getPart('year'),
    month: getPart('month'),
    day: getPart('day'),
    hour: getPart('hour'),
    minute: getPart('minute'),
    second: getPart('second'),
  };
};

const getTimeZoneOffsetMs = (date: Date, timeZone: string): number => {
  const parts = getDateTimePartsInTimeZone(date, timeZone);
  const zonedTimeAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );

  return zonedTimeAsUtc - date.getTime();
};

const formatHHMMInTimeZone = (date: Date, timeZone: string): string => {
  const parts = getDateTimePartsInTimeZone(date, timeZone);
  return `${padTimePart(parts.hour)}:${padTimePart(parts.minute)}`;
};

const getUTCTimeReferenceDate = (companyTimezone: string): string => {
  return getTodayDateInCompanyTimezone(companyTimezone || 'UTC');
};

const buildUTCDateFromTimeOnly = (timeString: string, referenceDate: string): Date | null => {
  const timeParts = parseTimeOnlyString(timeString);
  if (!timeParts) return null;

  return new Date(`${referenceDate}T${formatTimeParts(timeParts)}Z`);
};

/**
 * Convert a UTC time-only string from the API into HH:mm in company timezone.
 * Time-only values have no date context, so today's company date is used for timezone offset rules.
 */
export const convertUTCTimeStringToCompanyTime = (
  utcTimeString: string,
  companyTimezone: string,
  referenceDate?: string
): string => {
  if (!utcTimeString) return '';

  try {
    const timeZone = companyTimezone || 'UTC';
    const date =
      buildUTCDateFromTimeOnly(utcTimeString, referenceDate || getUTCTimeReferenceDate(timeZone)) ||
      new Date(normalizeToUTC(utcTimeString));

    if (Number.isNaN(date.getTime())) return utcTimeString.length > 5 ? utcTimeString.substring(0, 5) : utcTimeString;

    return formatHHMMInTimeZone(date, timeZone);
  } catch (error) {
    console.error('Error converting UTC time string to company timezone:', error);
    return utcTimeString.length > 5 ? utcTimeString.substring(0, 5) : utcTimeString;
  }
};

/**
 * Convert an HH:mm company-local time from the UI into HH:mm UTC for the API.
 * Time-only values have no date context, so today's company date is used for timezone offset rules.
 */
export const convertCompanyTimeStringToUTCTime = (
  companyTimeString: string,
  companyTimezone: string,
  referenceDate?: string
): string => {
  if (!companyTimeString) return '';

  try {
    const timeZone = companyTimezone || 'UTC';
    const timeParts = parseTimeOnlyString(companyTimeString);
    if (!timeParts) {
      const parsedDate = new Date(companyTimeString);
      if (Number.isNaN(parsedDate.getTime())) {
        return companyTimeString.length > 5 ? companyTimeString.substring(0, 5) : companyTimeString;
      }
      return `${padTimePart(parsedDate.getUTCHours())}:${padTimePart(parsedDate.getUTCMinutes())}`;
    }

    const dateString = referenceDate || getUTCTimeReferenceDate(timeZone);
    const localWallTimeAsUtc = Date.UTC(
      Number(dateString.substring(0, 4)),
      Number(dateString.substring(5, 7)) - 1,
      Number(dateString.substring(8, 10)),
      timeParts.hour,
      timeParts.minute,
      timeParts.second
    );

    let utcTime = localWallTimeAsUtc;
    for (let i = 0; i < 2; i += 1) {
      utcTime = localWallTimeAsUtc - getTimeZoneOffsetMs(new Date(utcTime), timeZone);
    }

    const utcDate = new Date(utcTime);
    return `${padTimePart(utcDate.getUTCHours())}:${padTimePart(utcDate.getUTCMinutes())}`;
  } catch (error) {
    console.error('Error converting company time string to UTC:', error);
    return companyTimeString.length > 5 ? companyTimeString.substring(0, 5) : companyTimeString;
  }
};

/**
 * Format date and time in company timezone
 * @param utcDateString - UTC date string from API
 * @param companyTimezone - Company timezone
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date and time string in company timezone
 */
export const formatDateTimeInCompanyTimezone = (
  utcDateString: string,
  companyTimezone: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  if (!utcDateString) return 'N/A';

  try {
    const normalized = normalizeToUTC(utcDateString);

    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: companyTimezone,
      ...options,
    };


    return new Date(normalized).toLocaleString('en-US', defaultOptions);
  } catch (error) {
    console.error('Error formatting date time in company timezone:', error);
    return 'N/A';
  }
};

/**
 * Format time only in company timezone
 * @param utcDateString - UTC date string from API
 * @param companyTimezone - Company timezone
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted time string in company timezone
 */
export const formatTimeInCompanyTimezone = (
  utcDateString: string, 
  companyTimezone: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  if (!utcDateString) return 'N/A';
  
  try {
    const companyDate = convertUTCToCompanyTimezone(utcDateString, companyTimezone);
    
    // Default options for time formatting
    const defaultOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...options
    };
    
    return companyDate.toLocaleTimeString('en-US', defaultOptions);
  } catch (error) {
    console.error('Error formatting time in company timezone:', error);
    return 'N/A';
  }
};

/**
 * Format a calendar time range with overnight indicator.
 * Returns e.g. "23:00 – 06:00 (+1)" for overnight, "08:00 – 16:00" for same-day.
 */
export const formatCalendarTimeRange = (
  checkIn: string,
  checkOut: string,
  spansNextDay?: boolean,
  companyTimezone?: string,
  nextDayLabel: string = '+1',
  separator: string = '–'
): string => {
  const inTime = companyTimezone
    ? convertUTCTimeStringToCompanyTime(checkIn, companyTimezone)
    : (checkIn?.length > 5 ? checkIn.substring(0, 5) : checkIn || '');
  const outTime = companyTimezone
    ? convertUTCTimeStringToCompanyTime(checkOut, companyTimezone)
    : (checkOut?.length > 5 ? checkOut.substring(0, 5) : checkOut || '');
  const nextDay = spansNextDay ? ` (${nextDayLabel})` : '';
  return `${inTime}${separator}${outTime}${nextDay}`;
};


/**
 * Get today's date in YYYY-MM-DD format for company timezone.
 * @param companyTimezone - Company timezone
 * @returns Date string in YYYY-MM-DD format
 */
export const getTodayDateInCompanyTimezone = (companyTimezone: string): string => {
  try {
    const now = new Date();
    const companyDate = new Date(now.toLocaleString('en-US', { timeZone: companyTimezone }));
    
    const year = companyDate.getFullYear();
    const month = String(companyDate.getMonth() + 1).padStart(2, '0');
    const day = String(companyDate.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error getting today date in company timezone:', error);
    // Fallback to local timezone
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
};

/**
 * Get current month in YYYY-MM format for company timezone
 * @param companyTimezone - Company timezone
 * @returns Month string in YYYY-MM format
 */
export const getCurrentMonthInCompanyTimezone = (companyTimezone: string): string => {
  try {
    const now = new Date();
    const companyDate = new Date(now.toLocaleString('en-US', { timeZone: companyTimezone }));
    
    const year = companyDate.getFullYear();
    const month = String(companyDate.getMonth() + 1).padStart(2, '0');
    
    return `${year}-${month}`;
  } catch (error) {
    console.error('Error getting current month in company timezone:', error);
    // Fallback to local timezone
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }
};

/**
 * Check if a date is today in company timezone
 * @param utcDateString - UTC date string from API
 * @param companyTimezone - Company timezone
 * @returns boolean indicating if the date is today
 */
export const isTodayInCompanyTimezone = (utcDateString: string, companyTimezone: string): boolean => {
  if (!utcDateString) return false;
  
  try {
    const companyDate = convertUTCToCompanyTimezone(utcDateString, companyTimezone);
    const today = new Date();
    const todayInCompanyTZ = new Date(today.toLocaleString('en-US', { timeZone: companyTimezone }));
    
    return (
      companyDate.getDate() === todayInCompanyTZ.getDate() &&
      companyDate.getMonth() === todayInCompanyTZ.getMonth() &&
      companyDate.getFullYear() === todayInCompanyTZ.getFullYear()
    );
  } catch (error) {
    console.error('Error checking if date is today in company timezone:', error);
    return false;
  }
};

/**
 * Extract the first YYYY-MM-DD date-only string from a value that may be
 * a bare date or a full ISO datetime.
 */
export const toDateOnly = (value: string): string => {
  return value.match(/\d{4}-\d{2}-\d{2}/)?.[0] || value;
};

const parseDateOnly = (value: string): Date | null => {
  const dateOnly = toDateOnly(value);
  const match = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31) return null;

  // Construct from calendar parts without interpreting the input as a UTC instant.
  // setFullYear avoids Date's special 1900 offset for years from 0 through 99.
  const date = new Date(0);
  date.setHours(0, 0, 0, 0);
  date.setFullYear(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

/**
 * Format a date-only value (YYYY-MM-DD or ISO string containing one)
 * as a human-readable calendar date.  Parses year/month/day locally
 * (no timezone conversion) so the displayed day always matches the
 * calendar day in the string.
 */
export const formatDateOnly = (value: string, locale?: string, options?: Intl.DateTimeFormatOptions): string => {
  if (!value) return value;
  try {
    const date = parseDateOnly(value);
    if (!date) return value;
    const formatOptions: Intl.DateTimeFormatOptions = options || {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return date.toLocaleDateString(locale || i18n.language, formatOptions);
  } catch {
    return value;
  }
};

/**
 * Format a UTC-midnight datetime (e.g. "2026-08-03T00:00:00.000000Z")
 * by extracting the UTC date part and displaying it as a calendar date
 * without any timezone conversion.
 */
export const formatUtcMidnightDate = (value: string, locale?: string): string => {
  if (!value) return value;
  const dateOnly = toDateOnly(value);
  if (!parseDateOnly(dateOnly)) return value;
  return formatDateOnly(dateOnly, locale);
};


